import { useEffect, useMemo, useState } from 'react';
import { DataLoanDefinitionTct, LoanDefinitionType } from '@dealstack/shared/lib/generated';
import {
	createPendingPerson,
	useLegalPersonSelect,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/Loans/components/ManageLoanDrawer/useLegalPersonSelect.ts';
import type { PendingLegalPerson } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/Loans/components/ManageLoanDrawer/useLegalPersonSelect.types';
import { useSecurityTypeTranslation } from '@dealstack/frontend/shared/translationControllers/useCommonTranslationsController.ts';

interface LoanFormData {
	id?: string;
	type: string;
	name: string;
	funderId: string;
	funderIsNew?: boolean;
	funderName?: string;
}

interface Props {
	dealId: string;
	opened: boolean;
	loanData: DataLoanDefinitionTct | null;
	onSave: (loan: LoanFormData) => void;
	onRemove: (loanId: string) => void;
	onClose: () => void;
}

export const useManageLoanDrawer = ({
	dealId,
	opened,
	loanData,
	onSave,
	onRemove,
	onClose,
}: Props) => {
	const [type, setType] = useState(loanData?.type || '');
	const [name, setName] = useState(loanData?.name || '');
	const [funderId, setFunderId] = useState(loanData?.funderId || '');
	const [pendingFunder, setPendingFunder] = useState<PendingLegalPerson | null>(null);
	const getSecurityTypeTranslation = useSecurityTypeTranslation();

	const loanTypeOptions = useMemo(
		() => [
			{
				value: LoanDefinitionType.MANAGEMENT_LOAN,
				label: getSecurityTypeTranslation(LoanDefinitionType.MANAGEMENT_LOAN),
			},
			{
				value: LoanDefinitionType.DEFERRED_PAYMENT,
				label: getSecurityTypeTranslation(LoanDefinitionType.DEFERRED_PAYMENT),
			},
		],
		[getSecurityTypeTranslation]
	);

	const { isLoadingPersons, personOptions, selectedPerson, refetchPersons } =
		useLegalPersonSelect({
			dealId,
			selectedPersonId: funderId,
			pendingPerson: pendingFunder,
		});

	useEffect(() => {
		if (opened && loanData) {
			setType(loanData.type || '');
			setName(loanData.name || '');
			setFunderId(loanData.funderId || '');
		} else if (opened && !loanData) {
			setType('');
			setName('');
			setFunderId('');
		}
		setPendingFunder(null);
	}, [opened, loanData]);

	const onFunderChange = (value: string | null) => {
		setPendingFunder((current) => (current && value === current.id ? current : null));
		setFunderId(value || '');
	};

	const onCreateNewFunder = (funderName: string) => {
		const pending = createPendingPerson(funderName);

		setPendingFunder(pending);
		setFunderId(pending.id);

		return pending.id;
	};

	const handleSave = () => {
		const updatedLoan: LoanFormData = {
			id: loanData?.id,
			type,
			name,
			funderId,
			funderIsNew: selectedPerson?.isNew,
			funderName: selectedPerson?.label,
		};

		onSave(updatedLoan);
		onClose();
	};

	const handleRemove = () => {
		if (loanData?.id) {
			onRemove(loanData.id);
		}
		onClose();
	};

	const isEditMode = !!loanData?.id;
	const isFormValid = !!type && !!name && !!funderId;

	return {
		type,
		setType,
		name,
		setName,
		funderId,
		onFunderChange,
		handleSave,
		handleRemove,
		isEditMode,
		isFormValid,
		funderOptions: personOptions,
		onCreateNewFunder,
		isLoadingFunders: isLoadingPersons,
		refetchFunders: refetchPersons,
		loanTypeOptions,
	};
};
